/**
 * Üst menü çubuğu kaç modülü nasıl gösterecek? Üç kademe:
 *
 *   1. adlı     — hepsi adıyla sığıyorsa (ve kullanıcı "yalnız simge" seçmediyse)
 *   2. simge    — adlarıyla sığmıyorsa hepsi yalnız simge (ad ipucunda)
 *   3. simge+…  — simge olarak da sığmıyorsa sığanlar simge, kalanı "…" menüsünde
 *
 * Adlı genişlikler ekrandaki düğmelerden DEĞİL, görünmez bir cetvelden okunur: cetvelde
 * bütün modüller her zaman adıyla durur. Ekrandakini ölçmek salınıma giriyordu (simgeye
 * düşünce yer açılıyor, adlar geri geliyor, yine taşıyor). Simge genişliği sabittir
 * (düğme `w-8`), ölçülmez.
 */
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';

export type TopNavMode = 'captions' | 'icons';

export interface TopNavFit {
  mode: TopNavMode;
  /** Çubukta duran modül sayısı; kalanı "…" menüsünde. */
  visible: number;
}

export interface TopNavFitOptions {
  /** Simge düğmesinin genişliği (px). */
  iconWidth: number;
  /** "…" düğmesine ayrılan genişlik (px). */
  overflowWidth: number;
  /** Öğeler arası boşluk (px). */
  gap: number;
  /** Kullanıcı tercihi: adlar sığsa da yalnız simge. */
  compact: boolean;
}

export function computeTopNavFit(
  available: number,
  captionWidths: number[],
  { iconWidth, overflowWidth, gap, compact }: TopNavFitOptions,
): TopNavFit {
  const n = captionWidths.length;
  const gaps = Math.max(0, n - 1) * gap;
  if (!compact && captionWidths.reduce((a, w) => a + w, 0) + gaps <= available) {
    return { mode: 'captions', visible: n };
  }
  if (n * iconWidth + gaps <= available) return { mode: 'icons', visible: n };
  // k simge + boşluk + "…": k·icon + (k−1)·gap + gap + overflow ≤ available
  const fits = Math.floor((available - overflowWidth) / (iconWidth + gap));
  return { mode: 'icons', visible: Math.max(1, Math.min(n - 1, fits)) };
}

export function useTopNavFit(
  containerRef: RefObject<HTMLElement | null>,
  rulerRef: RefObject<HTMLElement | null>,
  count: number,
  { iconWidth, overflowWidth, gap, compact }: TopNavFitOptions,
): TopNavFit {
  const [fit, setFit] = useState<TopNavFit>({ mode: compact ? 'icons' : 'captions', visible: count });

  const recompute = useCallback(() => {
    const el = containerRef.current;
    const ruler = rulerRef.current;
    if (!el || !ruler) return;
    // Gizli kap (mobil, başka yerleşim): ölçüm anlamsız.
    const available = el.clientWidth;
    if (available <= 0) return;
    const widths = Array.from(ruler.children, (c) => (c as HTMLElement).offsetWidth);
    // Yazı tipi / ikon henüz yüklenmediyse genişlikler 0: kesinleşmesin, ResizeObserver tazeler.
    if (widths.length < count || widths.some((w) => !(w > 0))) return;
    const next = computeTopNavFit(available, widths.slice(0, count), {
      iconWidth,
      overflowWidth,
      gap,
      compact,
    });
    setFit((prev) => (prev.mode === next.mode && prev.visible === next.visible ? prev : next));
  }, [containerRef, rulerRef, count, iconWidth, overflowWidth, gap, compact]);

  // Boyama öncesi: kullanıcı önce taşan, sonra kesilen bir çubuk görmesin.
  useLayoutEffect(() => {
    recompute();
  }, [recompute]);

  useEffect(() => {
    const el = containerRef.current;
    const ruler = rulerRef.current;
    if (!el || !ruler || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => recompute());
    ro.observe(el);
    for (const child of Array.from(ruler.children)) ro.observe(child);
    return () => ro.disconnect();
  }, [containerRef, rulerRef, recompute]);

  return fit;
}
